import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bb_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchContacts = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: { "X-Admin-Token": t },
      });
      setContacts(res.data);
    } catch (e) {
      toast.error("Failed to load contacts. Check token.");
      if (e.response?.status === 401) {
        setToken("");
        localStorage.removeItem(TOKEN_KEY);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchContacts(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { token: tokenInput });
      if (res.data?.ok) {
        localStorage.setItem(TOKEN_KEY, tokenInput);
        setToken(tokenInput);
        toast.success("Logged in");
      }
    } catch (err) {
      toast.error("Invalid token");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setContacts([]);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/contacts/${id}`,
        { status },
        { headers: { "X-Admin-Token": token } }
      );
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success("Status updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-bg-ivory flex items-center justify-center px-6" dir="ltr">
        <form
          onSubmit={login}
          className="w-full max-w-md p-10 border border-brand-green/15 bg-white"
          data-testid="admin-login-form"
        >
          <div className="label-meta text-brand-green/55 mb-3">[ ADMIN ]</div>
          <h1 className="serif-title text-brand-green mb-2" style={{ fontSize: 48 }}>
            BigBrand Admin
          </h1>
          <p className="text-brand-green/65 mb-8" style={{ fontSize: 14 }}>
            Enter the admin token to view submissions.
          </p>
          <input
            type="password"
            className="bb-input mb-8"
            placeholder="Admin token"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            data-testid="admin-token-input"
          />
          <button type="submit" className="bb-btn bb-btn-primary w-full" data-testid="admin-login-btn">
            Login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-ivory" dir="ltr" data-testid="admin-dashboard">
      <header
        className="px-6 md:px-10 lg:px-14 py-6 bg-[#173F35] text-bg-ivory"
        style={{ borderBottom: "1px solid rgba(232,211,195,0.18)" }}
      >
        <div className="max-w-[1760px] mx-auto flex items-center justify-between">
          <div>
            <div className="label-meta text-champagne/80 mb-1">[ ADMIN ]</div>
            <BrandLogo height={24} theme="light" />
          </div>
          <div className="flex items-center gap-6">
            <div className="label-meta text-bg-ivory/65">{contacts.length} SUBMISSIONS</div>
            <button onClick={logout} className="bb-btn bb-btn-ghost-dark" data-testid="admin-logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14 py-12">
        {loading ? (
          <div className="py-20 text-center text-brand-green/65">Loading...</div>
        ) : contacts.length === 0 ? (
          <div className="py-20 text-center text-brand-green/65">No submissions yet.</div>
        ) : (
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5 lg:col-span-4 space-y-2 max-h-[80vh] overflow-y-auto no-scrollbar">
              {contacts.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className={`w-full text-start p-5 border transition-all duration-300 ${
                    selected?.id === c.id
                      ? "bg-[#173F35] text-bg-ivory border-[#173F35]"
                      : "bg-white border-brand-green/15 text-brand-green hover:border-brand-green/40"
                  }`}
                  data-testid={`contact-row-${c.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{c.name}</div>
                    <span
                      className="label-meta"
                      style={{
                        fontSize: 10,
                        color: c.status === "new" ? "#E8D3C3" : "rgba(232,211,195,0.6)",
                      }}
                    >
                      {c.status}
                    </span>
                  </div>
                  <div className="opacity-75" style={{ fontSize: 12 }}>{c.email}</div>
                  <div className="opacity-65 mt-1" style={{ fontSize: 11 }}>
                    {c.company || "—"}
                  </div>
                  <div className="opacity-55 mt-2" style={{ fontSize: 10 }}>
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </button>
              ))}
            </div>

            <div className="col-span-12 md:col-span-7 lg:col-span-8">
              {selected ? (
                <div className="bg-white border border-brand-green/15 p-8 md:p-10" data-testid="contact-detail">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="label-meta text-brand-green/55 mb-1">[ {selected.id.slice(0, 8)} ]</div>
                      <h2 className="serif-title text-brand-green" style={{ fontSize: 38 }}>{selected.name}</h2>
                      <a href={`mailto:${selected.email}`} className="text-brand-green/75 underline">
                        {selected.email}
                      </a>
                    </div>
                    <select
                      value={selected.status}
                      onChange={(e) => {
                        updateStatus(selected.id, e.target.value);
                        setSelected({ ...selected, status: e.target.value });
                      }}
                      className="bb-input"
                      style={{ width: 160, paddingBottom: 8 }}
                      data-testid="status-select"
                    >
                      <option value="new">new</option>
                      <option value="in_review">in_review</option>
                      <option value="contacted">contacted</option>
                      <option value="closed">closed</option>
                    </select>
                  </div>

                  <div className="editorial-rule my-6" />

                  <div className="grid grid-cols-2 gap-4 mb-6 text-brand-green/85" style={{ fontSize: 13 }}>
                    <div>
                      <div className="label-meta text-brand-green/55 mb-1">COMPANY</div>
                      <div>{selected.company || "—"}</div>
                    </div>
                    <div>
                      <div className="label-meta text-brand-green/55 mb-1">PROJECT TYPE</div>
                      <div>{selected.project_type || "—"}</div>
                    </div>
                    <div>
                      <div className="label-meta text-brand-green/55 mb-1">PROJECT SIZE</div>
                      <div>{selected.project_size || "—"}</div>
                    </div>
                    <div>
                      <div className="label-meta text-brand-green/55 mb-1">LANGUAGE</div>
                      <div>{selected.language?.toUpperCase()}</div>
                    </div>
                  </div>

                  <div className="editorial-rule my-6" />

                  <div className="label-meta text-brand-green/55 mb-3">MESSAGE</div>
                  <p
                    className={`text-brand-green ${selected.language === "ar" ? "arabic-title" : ""}`}
                    style={{ fontSize: 15, lineHeight: 1.8, whiteSpace: "pre-wrap" }}
                    dir={selected.language === "ar" ? "rtl" : "ltr"}
                  >
                    {selected.message}
                  </p>
                </div>
              ) : (
                <div className="bg-white border border-brand-green/15 p-12 text-brand-green/55 text-center">
                  Select a submission to view details.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
